import type { ClassDef } from '../schemas/defs.js';

/**
 * The ten classes and their 2 / 4 / 6 synergy tiers (GDD §6).
 *
 * Effects from EVERY tier at or below the current count are active (TRD-08 R-08-03): at
 * count 5 both the tier-2 and tier-4 effect sets apply.
 *
 * Design rule, enforced by review rather than by code: every threshold must be legible in
 * one sentence AND visible on screen. If the player cannot see the synergy fire, it is not
 * a synergy — it is a spreadsheet.
 *
 * ── Two GDD corrections found while encoding this ──────────────────────────────────────
 *
 * 1. Warden-2 originally read "Wardens gain 25% damage reduction from AoE". Towers are
 *    never damaged in this game, so the effect was unreachable. Rewritten around Warden's
 *    real identity: the Core and the leaks that reach it.
 * 2. Beacon-6 originally read "+1 placement every 3rd round". Per-Nth-round conditionality
 *    has exactly one user and would need a union variant carrying wave-counter state.
 *    Simplified to a flat +1 placement per round — same intent, one less mechanism.
 */
export const CLASSES: ClassDef[] = [
  {
    id: 'warden',
    name: 'Warden',
    color: '#8a9bb0',
    accentColor: '#c3d0dd',
    glyph: 'W',
    description: 'Fortifies the Core and drags enemies to a crawl around itself.',
    tiers: [
      {
        threshold: 2,
        summary: '+2 Core HP. Enemies within 2 tiles of a Warden are slowed 10%.',
        effects: [
          { k: 'runStat', stat: 'bonusCoreHp', op: 'add', value: 2 },
          {
            k: 'auraStatus',
            radiusTiles: 2,
            status: 'chill',
            magnitude: 0.1,
            durationTicks: 30,
          },
        ],
      },
      {
        threshold: 4,
        summary: '+4 Core HP instead. Warden slow-field deepens to 20%.',
        effects: [
          { k: 'runStat', stat: 'bonusCoreHp', op: 'add', value: 2 },
          {
            k: 'auraStatus',
            radiusTiles: 2,
            status: 'chill',
            magnitude: 0.2,
            durationTicks: 30,
          },
        ],
      },
      {
        threshold: 6,
        summary: 'Every leak costs exactly 1 Core HP, no matter what leaked.',
        // Shipped as `bonusCoreHp +0` — a capstone that did nothing at all, because the
        // engine field this needs was typed `number | null` and therefore invisible to
        // `runStat`. Six Wardens is the heaviest commitment in the game and it bought
        // literally zero.
        effects: [{ k: 'runStat', stat: 'leakDamageOverride', op: 'set', value: 1 }],
      },
    ],
  },

  {
    id: 'marksman',
    name: 'Marksman',
    color: '#e8a33d',
    accentColor: '#ffd27f',
    glyph: 'M',
    description: 'Long range and heavy single-target punishment.',
    tiers: [
      {
        threshold: 2,
        summary: '+15% range to Marksmen.',
        effects: [
          {
            k: 'stat',
            target: { s: 'sameClass' },
            stat: 'rangeMultiplier',
            op: 'mul',
            value: 1.15,
          },
        ],
      },
      {
        threshold: 4,
        summary: 'Marksmen crit for 2x on 15% of shots.',
        effects: [
          { k: 'stat', target: { s: 'sameClass' }, stat: 'critChance', op: 'add', value: 0.15 },
        ],
      },
      {
        threshold: 6,
        summary: 'Every 6th Marksman shot is a railshot: pierces 3 and hits twice as hard.',
        effects: [
          {
            k: 'everyNthShot',
            n: 6,
            then: [{ k: 'shotModifier', pierceAdd: 3, damageMultiplier: 2 }],
          },
        ],
      },
    ],
  },

  {
    id: 'arcanist',
    name: 'Arcanist',
    color: '#9b5de5',
    accentColor: '#c99cf5',
    glyph: 'A',
    description: 'Cuts through armor and turns marked targets into bombs.',
    tiers: [
      {
        threshold: 2,
        summary: 'Arcanist damage ignores 50% of armor.',
        effects: [
          { k: 'stat', target: { s: 'sameClass' }, stat: 'armorPierce', op: 'add', value: 0.5 },
        ],
      },
      {
        threshold: 4,
        // Vulnerability is a property of the TARGET, applied before any damage-kind
        // check, so the bonus is not magic-only however it reads.
        summary: 'Arcanist hits Mark the target. Marked enemies take +20% damage.',
        effects: [
          { k: 'applyStatus', on: 'hit', status: 'mark', magnitude: 1, durationTicks: 240 },
          { k: 'bonusVsStatus', status: 'mark', damageMultiplier: 1.2 },
        ],
      },
      {
        threshold: 6,
        summary: 'Killing a Marked enemy detonates it for 40% of its max HP in 2 tiles.',
        effects: [
          {
            k: 'onKillExplode',
            radiusTiles: 2,
            percentMaxHp: 0.4,
            kind: 'magic',
            requireStatus: 'mark',
          },
        ],
      },
    ],
  },

  {
    id: 'venom',
    name: 'Venom',
    color: '#8ac926',
    accentColor: '#c5f26a',
    glyph: 'V',
    description: 'Stacks poison that bypasses armor and spreads on death.',
    tiers: [
      {
        threshold: 2,
        summary: 'Attacks apply 1 Poison stack: 2 true damage per second for 4s, up to 5.',
        effects: [
          {
            k: 'applyStatus',
            on: 'hit',
            status: 'poison',
            magnitude: 2,
            durationTicks: 240,
            stacks: 1,
          },
        ],
      },
      {
        threshold: 4,
        summary: 'Poison stacks to 10 and ticks twice as fast.',
        effects: [{ k: 'statusTuning', status: 'poison', capOverride: 10, tickIntervalTicks: 15 }],
      },
      {
        threshold: 6,
        summary: 'Enemies dying with 5+ stacks burst, applying 3 stacks within 3 tiles.',
        effects: [
          {
            k: 'onKillSpreadStatus',
            status: 'poison',
            radiusTiles: 3,
            stacks: 3,
            requireStacks: 5,
          },
        ],
      },
    ],
  },

  {
    id: 'frost',
    name: 'Frost',
    color: '#4cc9f0',
    accentColor: '#a8e6f7',
    glyph: 'F',
    description: 'Slows the field and makes chilled enemies fragile.',
    tiers: [
      {
        threshold: 2,
        summary: 'Attacks Chill: -15% speed for 1.5s.',
        effects: [
          { k: 'applyStatus', on: 'hit', status: 'chill', magnitude: 0.15, durationTicks: 90 },
        ],
      },
      {
        threshold: 4,
        summary: 'Chill deepens to -30%. Chilled enemies take +15% damage from all sources.',
        effects: [
          { k: 'applyStatus', on: 'hit', status: 'chill', magnitude: 0.3, durationTicks: 90 },
          { k: 'bonusVsStatus', status: 'chill', damageMultiplier: 1.15 },
        ],
      },
      {
        threshold: 6,
        summary: 'Every 5s the whole field Freezes for 0.75s.',
        effects: [
          {
            k: 'periodic',
            everyTicks: 300,
            then: [{ k: 'fieldStatus', status: 'freeze', durationTicks: 45, magnitude: 1 }],
          },
        ],
      },
    ],
  },

  {
    id: 'tempest',
    name: 'Tempest',
    color: '#dbe9ff',
    accentColor: '#ffffff',
    glyph: 'T',
    description: 'Chains between targets and stuns what it keeps hitting.',
    tiers: [
      {
        threshold: 2,
        summary: 'Tempest attacks chain to 1 extra target for 50% damage.',
        effects: [
          { k: 'stat', target: { s: 'sameClass' }, stat: 'chainCount', op: 'add', value: 1 },
        ],
      },
      {
        threshold: 4,
        summary: 'Chains to 3 targets. +20% attack speed to Tempests.',
        effects: [
          { k: 'stat', target: { s: 'sameClass' }, stat: 'chainCount', op: 'add', value: 2 },
          {
            k: 'stat',
            target: { s: 'sameClass' },
            stat: 'attackSpeedMultiplier',
            op: 'mul',
            value: 1.2,
          },
        ],
      },
      {
        threshold: 6,
        summary: 'Chains apply Shock. At 3 Shocks the enemy is stunned for 1s.',
        effects: [
          {
            k: 'applyStatus',
            on: 'hit',
            status: 'shock',
            magnitude: 1,
            durationTicks: 180,
            stacks: 1,
          },
        ],
      },
    ],
  },

  {
    id: 'alchemist',
    name: 'Alchemist',
    color: '#ffd60a',
    accentColor: '#fff3a0',
    glyph: 'G',
    description: 'Turns the board into an income engine.',
    tiers: [
      {
        threshold: 2,
        summary: '+1 gold per wave cleared.',
        effects: [{ k: 'runStat', stat: 'goldPerWave', op: 'add', value: 1 }],
      },
      {
        threshold: 4,
        summary: 'Interest cap raised to 10. Reroll costs half.',
        effects: [
          { k: 'runStat', stat: 'interestCap', op: 'set', value: 10 },
          { k: 'runStat', stat: 'rerollCostMultiplier', op: 'mul', value: 0.5 },
        ],
      },
      {
        threshold: 6,
        summary: '8% chance on kill to drop 1 gold.',
        effects: [{ k: 'gold', amount: 1, trigger: 'kill', chance: 0.08 }],
      },
    ],
  },

  {
    id: 'beacon',
    name: 'Beacon',
    color: '#f15bb5',
    accentColor: '#ffa8d8',
    glyph: 'B',
    description: 'Buffs its neighbours. Rewards tight, deliberate packing.',
    tiers: [
      {
        threshold: 2,
        // B-2a: "adjacent" was a lie next to a ranged tower — the buff now covers the
        // Beacon's own printed range, so its range circle IS the aura circle.
        summary: 'Beacons grant towers within their range +10% attack speed.',
        effects: [
          {
            k: 'aura',
            radiusTiles: 1.5,
            radiusFromRange: true,
            stat: 'attackSpeedMultiplier',
            op: 'mul',
            value: 1.1,
          },
        ],
      },
      {
        threshold: 4,
        // Tiers STACK, so the tier-2 aura is still running: attack speed compounds to
        // 1.1 x 1.1. The summary says the total, not the increment.
        summary: 'The in-range aura reaches further: +21% attack speed and +10% damage.',
        effects: [
          {
            k: 'aura',
            radiusTiles: 2,
            radiusFromRange: true,
            stat: 'attackSpeedMultiplier',
            op: 'mul',
            value: 1.1,
          },
          {
            k: 'aura',
            radiusTiles: 2,
            radiusFromRange: true,
            stat: 'damageMultiplier',
            op: 'mul',
            value: 1.1,
          },
        ],
      },
      {
        threshold: 6,
        summary: '+1 placement every round.',
        effects: [{ k: 'runStat', stat: 'bonusPlacements', op: 'add', value: 1 }],
      },
    ],
  },

  {
    id: 'swarm',
    name: 'Swarm',
    // Was lime #70e000, which sat ΔE 21.8 from Venom's acid — below the 25 floor and
    // caught by V-11. Two greens on identical silhouettes is exactly the failure that
    // rule exists to prevent. Emerald keeps the "living swarm" read at ΔE 45.5.
    color: '#00d084',
    accentColor: '#6ff0b8',
    glyph: 'S',
    description: 'Floods the field with short-lived drones.',
    tiers: [
      {
        threshold: 2,
        summary: 'Swarm towers summon a Drone every 6s (10 damage, 12s life).',
        effects: [
          {
            k: 'summon',
            everyTicks: 360,
            flatDamage: 10,
            damageFromOwnerPercent: 0,
            lifetimeTicks: 720,
          },
        ],
      },
      {
        threshold: 4,
        summary: 'Drones spawn every 3s and inherit 25% of the tower damage.',
        effects: [
          {
            k: 'summon',
            everyTicks: 180,
            flatDamage: 10,
            damageFromOwnerPercent: 0.25,
            lifetimeTicks: 720,
          },
        ],
      },
      {
        threshold: 6,
        // "when they expire" was the authored wording and it made the tier unreachable:
        // a drone only times out when nothing is within seek range, so the blast never
        // had anything to hit. Measured at exactly 0 damage across every seed.
        summary: 'Drones detonate for 25 AoE damage, splashing everything near their target.',
        effects: [{ k: 'summonExpiryExplode', radiusTiles: 1.5, damage: 25 }],
      },
    ],
  },

  {
    id: 'void',
    name: 'Void',
    color: '#ff006e',
    accentColor: '#ff7ab0',
    glyph: 'X',
    description: 'Scales off enemy health and deletes anything already dying.',
    tiers: [
      {
        threshold: 2,
        summary: 'Void attacks deal +1% of the target max HP.',
        effects: [
          {
            k: 'stat',
            target: { s: 'sameClass' },
            stat: 'percentMaxHpDamage',
            op: 'add',
            value: 0.01,
          },
        ],
      },
      {
        threshold: 4,
        summary: 'Void attacks apply Wither: -20% armor for 3s.',
        effects: [
          { k: 'applyStatus', on: 'hit', status: 'wither', magnitude: 0.2, durationTicks: 180 },
        ],
      },
      {
        threshold: 6,
        summary: 'Void executes enemies below 12% max HP instantly.',
        effects: [{ k: 'executeBelow', percentMaxHp: 0.12 }],
      },
    ],
  },
];
